import { all, call, put, select, takeLatest } from "redux-saga/effects";
import {
  fetchActivityTotals,
  fetchRelationships,
  fetchWorkkardTotals,
} from "../../api";
import {
  fetchRelationshipsFailure,
  fetchRelationshipsRequest,
  fetchRelationshipsSuccess,
} from "./relationshipsSlice";
import { getOrgId } from "../../../../utils/authManager";

const DEFAULT_PAGE_SIZE = 100;

/**
 * Extracts the primary list of business organizations from the relationships response.
 * @param {object} response - The API response from fetchRelationships.
 * @returns {Array} An array of relationship items.
 */
const extractRelationshipsData = (response = {}) => {
  return Array.isArray(response.business_orgs) ? response.business_orgs : [];
};

/**
 * Extracts the total count of items from the response.
 * @param {object} response - The API response.
 * @param {Array} normalizedData - The extracted data array, used as a fallback for total count.
 * @returns {number} The total number of items.
 */
const extractTotal = (response = {}, normalizedData = []) => {
  return typeof response.total === "number"
    ? response.total
    : normalizedData.length;
};

/**
 * Ensures a value is a number, returning 0 if it's not a valid number.
 * @param {*} value - The value to parse.
 * @returns {number} The parsed number or 0.
 */
const ensureNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Normalizes an array of totals into a map keyed by organizationId.
 * @param {object} response - The API response for totals.
 * @returns {object} A map of totals, e.g., { orgId: { open: 5, close: 10 } }.
 */
const normalizeTotalsMap = (response = {}) => {
  const entries = Array.isArray(response.data) ? response.data : [];
  return entries.reduce((map, entry) => {
    if (entry.organizationId) {
      map[entry.organizationId] = entry;
    }
    return map;
  }, {});
};

/**
 * Normalizes community access data into a map keyed by target_org_id.
 * @param {object} response - The API response containing community data.
 * @returns {object} A map where keys are organization IDs and values are arrays of community entries.
 */
const normalizeCommunityMap = (response = {}) => {
  const entries = Array.isArray(response.communities)
    ? response.communities
    : [];
  return entries.reduce((map, entry) => {
    const { target_org_id: targetOrgId } = entry;
    if (!targetOrgId) {
      return map;
    }
    if (!map[targetOrgId]) {
      map[targetOrgId] = [];
    }
    map[targetOrgId].push(entry);
    return map;
  }, {});
};

/**
 * Determines the relationship context, identifying the "other" organization.
 * @param {object} item - The raw relationship item.
 * @param {number} currentOrgId - The ID of the current user's organization.
 * @returns {{otherOrg: object, otherOrgId: number}}
 */
const getRelationshipContext = (item, currentOrgId) => {
  const isCurrentOrgAssignee = item.assignee === currentOrgId;
  const otherOrg = isCurrentOrgAssignee
    ? item.source_org || {}
    : item.destination_org || {};
  const otherOrgId =
    otherOrg.id || (isCurrentOrgAssignee ? item.owner : item.assignee);
  return { otherOrg, otherOrgId };
};

/**
 * Normalizes the B2B status for display, especially for 'PENDING' states.
 * @param {object} item - The raw relationship item.
 * @param {number} currentOrgId - The ID of the current user's organization.
 * @returns {string} The normalized status string (e.g., 'PENDING_TO_ACCEPT').
 */
const getNormalizedStatus = (item, currentOrgId) => {
  const rawStatus = (
    item.status ||
    item.destination_org?.B2BStatus ||
    item.RequestStatus ||
    ""
  ).toUpperCase();

  if (rawStatus === "PENDING") {
    if (currentOrgId === item.owner) {
      return "PENDING_TO_ACCEPT";
    }
    if (currentOrgId === item.assignee) {
      return "PENDING_FROM_RECEIVER";
    }
  }
  return rawStatus;
};

/**
 * Creates a factory function to check user permissions.
 * @param {object} userInfo - The user info object containing permissions.
 * @returns {function(string): boolean} A function that takes a permission key and returns a boolean.
 */
const createPermissionChecker = (userInfo) => (key) =>
  Boolean(userInfo?.permissions?.[key]);

/**
 * Gathers community-related details and permissions for a relationship.
 * @param {Array} communityList - The list of community entries for the organization.
 * @param {number} otherOrgId - The ID of the other organization in the relationship.
 * @param {number} currentOrgId - The ID of the current user's organization.
 * @returns {object} An object containing community-related flags and data.
 */
const getCommunityDetails = (communityList = [], otherOrgId, currentOrgId) => {
  const hasCommunityNotePermission = communityList.some(
    (entry) => entry.access_type_value === "add_b2b_workkard_note_permission",
  );

  const shareWkEntry = communityList.find(
    (entry) =>
      [1, 2, 4].includes(entry.access_type_id) &&
      entry.target_org_id === otherOrgId &&
      entry.org_id === currentOrgId,
  );

  const canShowDisableWorkkardNote = communityList.some(
    (entry) =>
      entry.access_type_id === 3 &&
      entry.target_org_id === otherOrgId &&
      entry.org_id === currentOrgId,
  );

  return {
    hasCommunityNotePermission,
    canRemoveShareWorkkard: Boolean(shareWkEntry),
    canShowDisableWorkkardNote,
    shareWkCommunityId: shareWkEntry?.id ?? null,
    shareWkAccessTypeId: shareWkEntry?.access_type_id ?? null,
  };
};

/**
 * Maps a raw relationship item to a structured object for the UI.
 * This function combines data from multiple sources to create a comprehensive view of a relationship.
 * @param {object} item - The raw relationship item from the API.
 * @param {object} workkardTotals - A map of workkard totals by organization ID.
 * @param {object} activityTotals - A map of activity totals by organization ID.
 * @param {object} communityAccess - A map of community access permissions.
 * @param {object} userInfo - The current user's information and permissions.
 * @param {number} currentOrgId - The ID of the current user's organization.
 * @returns {object} The processed relationship item for the UI.
 */
const mapRelationshipItem = (
  item = {},
  workkardTotals = {},
  activityTotals = {},
  communityAccess = {},
  userInfo = {},
  currentOrgId = null
) => {
  try{
  const { otherOrg, otherOrgId } = getRelationshipContext(item, currentOrgId);
  const b2bStatus = getNormalizedStatus(item, currentOrgId);

  const workkardTotalsForOrg = workkardTotals[otherOrgId] || {};
  const activityTotalsForOrg = activityTotals[otherOrgId] || {};
  const communityList = communityAccess[otherOrgId] || [];

  const communityDetails = getCommunityDetails(
    communityList,
    otherOrgId,
    currentOrgId,
  );
  const hasPermission = createPermissionChecker(userInfo);

  const canShareWorkkards =
    hasPermission("can_view_b2b_workload") &&
    hasPermission("invite_people_to_access_community") &&
    process.env.foo_share_workkard === "true";

  const canAddB2BWorkkardNote =
    b2bStatus === "ACCEPTED" || communityDetails.hasCommunityNotePermission;

  return {
    id: item.id,
    OrganizationId: otherOrg.id,
    OrganizationName: otherOrg.name || "",
    OrganizationImage: otherOrg.profile_image || "",
    Address1: otherOrg.address_line1,
    Address2: otherOrg.address_line2,
    City: otherOrg.city,
    State: otherOrg.state,
    Zip: otherOrg.zip,
    County: otherOrg.county,
    RequestStatus: b2bStatus,
    PendingCount: item.destination_org?.PendingCount ?? 1,
    ExpiredCount: item.destination_org?.ExpiredCount ?? 1,
    OpenWorkkards: ensureNumber(
      workkardTotalsForOrg.open ?? otherOrg.OpenWorkkards ?? 0,
    ),
    CloseWorkkards: ensureNumber(
      workkardTotalsForOrg.close ?? otherOrg.CloseWorkkards ?? 0,
    ),
    OpenActivities: ensureNumber(
      activityTotalsForOrg.open ?? otherOrg.OpenActivities ?? 0,
    ),
    CloseActivities: ensureNumber(
      activityTotalsForOrg.close ?? otherOrg.CloseActivities ?? 0,
    ),
    DestinationOrg: item.destination_org || {},
    SourceOrg: item.source_org || {},
    RelationshipId: item.id,
    CanShareWorkkards: canShareWorkkards,
    CanAddB2BWorkkardNote: canAddB2BWorkkardNote,
    CanManageContracts: hasPermission("can_manage_contracts"),
    CanSendB2BRelationshipRequest: hasPermission(
      "can_send_b2b_relationship_request",
    ),
    CanAcceptRejectB2BRelationshipRequest: hasPermission(
      "can_accept_reject_b2b_relationship_request",
    ),
    CanDeAssociateB2BRelationship: hasPermission(
      "can_de_associate_b2b_relationship",
    ),
    CanViewB2BWorkload: hasPermission("can_view_b2b_workload"),
    CanShowDisableWorkkardNote: communityDetails.canShowDisableWorkkardNote,
    CanRemoveShareWorkkard: communityDetails.canRemoveShareWorkkard,
    ShareWkCommunityId: communityDetails.shareWkCommunityId,
    ShareWkAccessTypeId: communityDetails.shareWkAccessTypeId,
  };
  } catch (error) {
    console.warn("Skipping malformed relationship item", item?.id, error);
    return null;
  }
};

/**
 * Saga helper to get the current user's permissions from Redux.
 * Permissions are no longer stored on manageProfile.userInfo, so they are
 * read from sessionContextOidc.userView.permissions instead.
 * @returns {object} An object containing the user's permissions.
 */
function* ensureUserInfo() {
  const permissions = yield select(
    (state) => state.sessionContextOidc?.userView?.permissions || {},
  );

  return { permissions };
}

/**
 * Fetches relationship data and related totals, then processes and stores it in Redux.
 * @param {object} action - The Redux action, containing payload with request parameters.
 */
export function* handleFetchRelationships(action) {
  const params = action.payload || {};
  try {
    const currentOrgId = Number(getOrgId());

    // Step 1: Ensure we have the necessary user info.
    const userInfo = yield call(ensureUserInfo);

    // Step 2: Fetch all required data in parallel.
    const [
      relationshipsResponse,
      workkardTotalsResponse,
      activityTotalsResponse,
    ] = yield all([
      call(fetchRelationships, params),
      call(fetchWorkkardTotals),
      call(fetchActivityTotals),
    ]);

    // Step 3: Normalize the fetched data for easier lookup.
    const workkardTotalsMap = normalizeTotalsMap(workkardTotalsResponse);
    const activityTotalsMap = normalizeTotalsMap(activityTotalsResponse);
    const communityMap = normalizeCommunityMap(relationshipsResponse);
    const rawRelationships = extractRelationshipsData(relationshipsResponse);

    // Step 4: Filter out internal-only relationship types.
    const filteredRelationships = rawRelationships.filter(
      (item) => (item?.status || "").toUpperCase() !== "EXCLUDED_SEARCH",
    );

    // Step 5: Map and enrich the relationship data, dropping any items that
    // failed to normalize (e.g. malformed entries from the API).
    const processedData = filteredRelationships
      .map((item) =>
        mapRelationshipItem(
          item,
          workkardTotalsMap,
          activityTotalsMap,
          communityMap,
          userInfo,
          currentOrgId,
        ),
      )
      .filter(Boolean);

    // Step 6: Prepare and dispatch the success action with paginated data.
    const { skip, take, rows, page } = params;
    const payload = {
      data: processedData,
      metadata: {
        total: extractTotal(relationshipsResponse, rawRelationships),
        skip: skip ?? 0,
        take: take ?? DEFAULT_PAGE_SIZE,
        rows: rows ?? take ?? DEFAULT_PAGE_SIZE,
        page: page ?? Math.floor((skip ?? 0) / (take || 1)) + 1,
      },
      params,
    };

    yield put(fetchRelationshipsSuccess(payload));
  } catch (error) {
    yield put(
      fetchRelationshipsFailure(error.message || "Failed to load relationships"),
    );
  }
}

export default function* relationshipsSaga() {
  yield takeLatest(fetchRelationshipsRequest.type, handleFetchRelationships);
}
